<script lang="ts">
  import { fade, fly } from "svelte/transition"
  import { onMount } from "svelte"
  import { spring } from "svelte/motion"
  import { supabase } from "$lib/supabase.js"

  let loaded = false
  let formSubmitted = false

  // Form data
  let name = ""
  let email = ""
  let company = ""
  let subject = ""
  let message = ""
  let formError = ""

  async function handleSubmit() {
    formError = ""

    // Simple validation
    if (!name || !email || !message) {
      formError = "Please fill out all required fields."
      return
    }

    try {
      // In a real implementation, you'd send this to your backend
      // await fetch('/api/contact', {
      //   method: 'POST',
      //   body: JSON.stringify({ name, email, company, subject, message }),
      //   headers: { 'Content-Type': 'application/json' }
      // });

      // For now, just simulate success
      formSubmitted = true

      // Reset form
      name = ""
      email = ""
      company = ""
      subject = ""
      message = ""
    } catch (error) {
      console.error("Error submitting form:", error)
      formError =
        "Sorry, there was a problem submitting your message. Please try again."
    }
  }

  onMount(() => {
    loaded = true
  })
</script>

<svelte:head>
  <title>Contact | Hollas AB</title>
  <meta
    name="description"
    content="Get in touch with Hollas AB to discuss your digital transformation, AI, or cybersecurity needs."
  />
</svelte:head>

<div class="min-h-screen bg-[#0F172A] text-white">
  <!-- Hero section -->
  <section class="py-20 bg-gradient-to-b from-base-200/50 to-transparent">
    <div class="container mx-auto px-6">
      {#if loaded}
        <h1
          class="text-4xl md:text-5xl font-bold mb-6 text-center"
          transition:fly={{ y: 20, duration: 600 }}
        >
          Get in <span class="text-primary">Touch</span>
        </h1>
        <p
          class="text-xl mb-8 max-w-3xl mx-auto text-center"
          transition:fly={{ y: 20, duration: 600, delay: 200 }}
        >
          Have a question or want to discuss a project? We'd love to hear from
          you. Fill out the form below and we'll get back to you as soon as
          possible.
        </p>
      {/if}
    </div>
  </section>

  <!-- Contact form section -->
  <section class="py-16">
    <div class="container mx-auto px-6">
      <div class="flex flex-col lg:flex-row gap-12">
        <!-- Contact information -->
        <div class="lg:w-1/3">
          <div class="card bg-base-200/70 h-full">
            <div class="card-body">
              <h2 class="text-2xl font-bold mb-6">Contact Information</h2>

              <div class="space-y-6">
                <div class="flex items-start gap-4">
                  <div class="text-primary mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-lg">Address</h3>
                    <address class="not-italic">
                      Pinnmovägen 3<br />
                      14139 Stuvsta<br />
                      Sweden
                    </address>
                  </div>
                </div>

                <div class="flex items-start gap-4">
                  <div class="text-primary mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-lg">Phone</h3>
                    <p>+46 73 949 90 99</p>
                  </div>
                </div>

                <div class="flex items-start gap-4">
                  <div class="text-primary mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-lg">Email</h3>
                    <p>
                      <a
                        href="mailto:contact@hollas.net"
                        class="text-primary hover:underline"
                        >contact@hollas.net</a
                      >
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-8">
                <h3 class="font-semibold text-lg mb-4">Connect With Us</h3>
                <div class="flex gap-4">
                  <a
                    href="https://linkedin.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-400 hover:text-primary transition-colors"
                    aria-label="LinkedIn"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                      />
                    </svg>
                  </a>
                  <a
                    href="https://twitter.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-400 hover:text-primary transition-colors"
                    aria-label="Twitter"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact form -->
        <div class="lg:w-2/3">
          {#if formSubmitted}
            <div class="card bg-success/20 h-full" in:fade={{ duration: 300 }}>
              <div
                class="card-body flex flex-col items-center justify-center text-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-16 w-16 text-success mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h2 class="text-2xl font-bold mb-2">Message Sent!</h2>
                <p class="mb-6">
                  Thank you for contacting us. We will get back to you as soon
                  as possible.
                </p>
                <button
                  class="btn btn-primary"
                  on:click={() => (formSubmitted = false)}
                  >Send Another Message</button
                >
              </div>
            </div>
          {:else}
            <div class="card bg-base-200/70">
              <div class="card-body">
                <h2 class="text-2xl font-bold mb-6">Send Us a Message</h2>

                <form on:submit|preventDefault={handleSubmit} class="space-y-4">
                  <div class="grid md:grid-cols-2 gap-6">
                    <div class="form-control">
                      <label for="name" class="label font-medium">
                        <span class="label-text text-white"
                          >Name <span class="text-error">*</span></span
                        >
                      </label>
                      <input
                        type="text"
                        id="name"
                        bind:value={name}
                        class="input input-bordered bg-base-300/50 w-full focus:border-primary"
                        required
                      />
                    </div>

                    <div class="form-control">
                      <label for="email" class="label font-medium">
                        <span class="label-text text-white"
                          >Email <span class="text-error">*</span></span
                        >
                      </label>
                      <input
                        type="email"
                        id="email"
                        bind:value={email}
                        class="input input-bordered bg-base-300/50 w-full focus:border-primary"
                        required
                      />
                    </div>
                  </div>

                  <div class="grid md:grid-cols-2 gap-6">
                    <div class="form-control">
                      <label for="company" class="label font-medium">
                        <span class="label-text text-white">Company</span>
                      </label>
                      <input
                        type="text"
                        id="company"
                        bind:value={company}
                        class="input input-bordered bg-base-300/50 w-full focus:border-primary"
                      />
                    </div>

                    <div class="form-control">
                      <label for="subject" class="label font-medium">
                        <span class="label-text text-white">Subject</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        bind:value={subject}
                        class="input input-bordered bg-base-300/50 w-full focus:border-primary"
                      />
                    </div>
                  </div>

                  <div class="form-control">
                    <label for="message" class="label font-medium">
                      <span class="label-text text-white"
                        >Message <span class="text-error">*</span></span
                      >
                    </label>
                    <textarea
                      id="message"
                      bind:value={message}
                      class="textarea textarea-bordered bg-base-300/50 w-full h-40 focus:border-primary"
                      required
                    ></textarea>
                  </div>

                  {#if formError}
                    <div class="alert alert-error" transition:fade>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="stroke-current shrink-0 h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        ><path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        /></svg
                      >
                      <span>{formError}</span>
                    </div>
                  {/if}

                  <button type="submit" class="btn btn-primary w-full md:w-auto"
                    >Send Message</button
                  >
                </form>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </section>
</div>
